import React, { useState } from 'react';

export function LandingPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState('');

  const openModal = (imageSrc: string) => {
    setModalImageSrc(imageSrc);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Close modal when clicking outside the image
  const handleModalClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // Close modal with Escape key
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      <style>{`
        :root{
          --brand:#02d96b;
          --brand-light:#4ade80;
          --brand-dark:#16a34a;
          --dark:#0f172a;
          --dark-light:#1e293b;
          --light:#ffffff;
          --gray:#64748b;
          --gray-light:#f1f5f9;
        }
        
        *{margin:0;padding:0;box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;}
        
        body{
          background:linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          color:var(--dark);
          line-height:1.6;
          overflow-x:hidden;
        }
        
        .btn{
          display:inline-block;
          background:linear-gradient(135deg, var(--brand) 0%, var(--brand-light) 100%);
          color:var(--dark);
          padding:16px 36px;
          border-radius:50px;
          font-weight:700;
          font-size:16px;
          text-decoration:none;
          transition:all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow:0 4px 15px rgba(2, 217, 107, 0.3);
          position:relative;
          overflow:hidden;
        }
        
        .btn::before{
          content:'';
          position:absolute;
          top:0;
          left:-100%;
          width:100%;
          height:100%;
          background:linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition:left 0.5s;
        }
        
        .btn:hover::before{
          left:100%;
        }
        
        .btn:hover{
          transform:translateY(-2px);
          box-shadow:0 8px 25px rgba(2, 217, 107, 0.4);
          background:linear-gradient(135deg, var(--brand-dark) 0%, var(--brand) 100%);
        }
        
        .login-buttons{display:flex;gap:16px;align-items:center;}
        
        header{
          background:rgba(255, 255, 255, 0.95);
          backdrop-filter:blur(20px);
          color:var(--dark);
          padding:24px 8%;
          box-shadow:0 4px 20px rgba(0,0,0,0.1);
        }
        
        nav{display:flex;align-items:center;justify-content:space-between;}
        
        .logo{
          font-weight:900;
          font-size:24px;
          align-self:flex-start;
        }
        
        .hero{
          display:flex;
          align-items:center;
          justify-content:center;
          flex-wrap:wrap;
          padding:120px 8%;
          text-align:center;
          position:relative;
          background:linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%);
        }
        
        .hero::before{
          content:'';
          position:absolute;
          top:0;
          left:0;
          right:0;
          bottom:0;
          background:radial-gradient(circle at 20% 80%, rgba(2, 217, 107, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(2, 217, 107, 0.1) 0%, transparent 50%);
          pointer-events:none;
        }
        
        .hero h1{
          font-size:clamp(36px, 5vw, 64px);
          font-weight:900;
          margin-bottom:24px;
          background:linear-gradient(135deg, var(--dark) 0%, var(--brand-dark) 100%);
          -webkit-background-clip:text;
          -webkit-text-fill-color:transparent;
          background-clip:text;
          line-height:1.2;
        }
        
        .hero p{
          font-size:clamp(18px, 2.5vw, 24px);
          margin-bottom:40px;
          color:var(--gray);
          max-width:600px;
          margin-left:auto;
          margin-right:auto;
        }
        
        .hero img{
          max-width:100%;
          width:480px;
          border-radius:20px;
          box-shadow:0 20px 40px rgba(0,0,0,0.15);
          transition:transform 0.3s ease;
          display:block;
          margin:0 auto;
        }
        
        .hero img:hover{
          transform:scale(1.02);
        }
        
        .steps{
          background:linear-gradient(135deg, var(--dark) 0%, var(--dark-light) 100%);
          color:var(--light);
          padding:120px 8%;
          position:relative;
        }
        
        .steps::before{
          content:'';
          position:absolute;
          top:0;
          left:0;
          right:0;
          bottom:0;
          background:url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.05)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
          pointer-events:none;
        }
        
        .steps h2{
          text-align:center;
          margin-bottom:60px;
          font-size:clamp(28px, 4vw, 48px);
          font-weight:800;
        }
        
        .grid{
          display:grid;
          gap:32px;
          grid-template-columns:repeat(auto-fit,minmax(320px,1fr));
        }
        
        .card{
          background:rgba(255,255,255,0.05);
          backdrop-filter:blur(20px);
          padding:40px;
          border-radius:20px;
          border:1px solid rgba(255,255,255,0.1);
          transition:all 0.3s ease;
          position:relative;
          overflow:hidden;
        }
        
        .card::before{
          content:'';
          position:absolute;
          top:0;
          left:0;
          right:0;
          height:4px;
          background:linear-gradient(90deg, var(--brand) 0%, var(--brand-light) 100%);
        }
        
        .card:hover{
          transform:translateY(-8px);
          box-shadow:0 20px 40px rgba(0,0,0,0.3);
        }
        
        .card h3{
          color:var(--brand);
          margin-bottom:16px;
          font-size:24px;
          font-weight:700;
        }
        
        .pricing{
          background:linear-gradient(135deg, var(--brand) 0%, var(--brand-light) 100%);
          color:var(--dark);
          padding:100px 8%;
          text-align:center;
          position:relative;
        }
        
        .pricing::before{
          content:'';
          position:absolute;
          top:0;
          left:0;
          right:0;
          bottom:0;
          background:radial-gradient(circle at center, rgba(255,255,255,0.1) 0%, transparent 70%);
          pointer-events:none;
        }
        
        .pricing h2{
          margin-bottom:24px;
          font-size:clamp(32px, 4vw, 48px);
          font-weight:800;
        }
        
        .pricing p{
          font-size:clamp(20px, 3vw, 32px);
          font-weight:700;
          margin-bottom:16px;
        }
        
        footer{
          background:linear-gradient(135deg, var(--dark) 0%, var(--dark-light) 100%);
          color:var(--light);
          padding:60px 8%;
          text-align:center;
        }
        
        footer a{
          color:var(--brand);
          text-decoration:none;
          margin-right:24px;
          font-weight:600;
          transition:color 0.3s ease;
        }
        
        footer a:hover{
          color:var(--brand-light);
        }
        
        @media(max-width:768px){
          .hero{
            flex-direction:column;
            text-align:center;
            padding:80px 4%;
          }
          
          .login-buttons{
            flex-direction:column;
            gap:12px;
          }
          
          .login-buttons .btn{
            width:100%;
            text-align:center;
          }
          
          .grid{
            grid-template-columns:1fr;
            gap:24px;
          }
          
          .card{
            padding:32px;
          }
        }
        
        /* Smooth scrolling */
        html{
          scroll-behavior:smooth;
        }
        
        /* Loading animation */
        @keyframes fadeInUp{
          from{
            opacity:0;
            transform:translateY(30px);
          }
          to{
            opacity:1;
            transform:translateY(0);
          }
        }
        
        .hero > *{
          animation:fadeInUp 0.8s ease-out;
        }
        
        .hero > *:nth-child(2){
          animation-delay:0.2s;
        }
        
        .hero > *:nth-child(3){
          animation-delay:0.4s;
        }
        
        /* Modal styles */
        .modal{
          display:${isModalOpen ? 'block' : 'none'};
          position:fixed;
          z-index:1000;
          left:0;
          top:0;
          width:100%;
          height:100%;
          background-color:rgba(0,0,0,0.9);
          backdrop-filter:blur(10px);
        }
        
        .modal-content{
          position:absolute;
          top:50%;
          left:50%;
          transform:translate(-50%, -50%);
          max-width:90%;
          max-height:90%;
          border-radius:20px;
          box-shadow:0 20px 60px rgba(0,0,0,0.5);
        }
        
        .close{
          position:absolute;
          top:20px;
          right:30px;
          color:#fff;
          font-size:40px;
          font-weight:bold;
          cursor:pointer;
          z-index:1001;
          transition:color 0.3s ease;
        }
        
        .close:hover{
          color:var(--brand);
        }
        
        .clickable{
          cursor:pointer;
          transition:all 0.3s ease;
        }
        
        .clickable:hover{
          transform:scale(1.05);
          box-shadow:0 25px 50px rgba(0,0,0,0.25);
        }
      `}</style>

      <header>
        <nav>
          <div className="logo">
            <img src="/assets/IMG/Branding.png" alt="Courier-RX Logo" style={{height: '340px', marginRight: '12px', verticalAlign: 'middle', background: 'white', padding: '4px', borderRadius: '4px'}} />
          </div>
          <div className="login-buttons">
            <a href="/auth/login?type=pharmacy" className="btn">Pharmacy Login</a>
            <a href="/auth/login?type=admin" className="btn">Admin Login</a>
            <a href="/demo-request" className="btn">Book Demo</a>
          </div>
        </nav>
      </header>

      <section className="hero">
        <div>
          <h1>Send Today.<br />Deliver Tomorrow.</h1>
          <p>One-click overnight delivery for every PrimeRX pharmacy. Zero setup, zero hassle.</p>
          <a href="/demo-request" className="btn">Book a Demo</a>
          <br />
          <img src="/assets/IMG/Courier-RX Dashboard.png" alt="Courier-RX Dashboard" className="clickable" onClick={() => openModal('/assets/IMG/Courier-RX Dashboard.png')} />
        </div>
      </section>

      <section className="hero" style={{background: 'var(--light)', color: 'var(--dark)'}}>
        <div>
          <h1>Track Every Mile.<br />Every Minute.</h1>
          <p>Real-time GPS tracking shows exactly where your delivery is, when it will arrive, and provides instant updates to your customers.</p>
          <a href="/demo-request" className="btn">See Live Tracking</a>
          <br />
          <img src="/assets/IMG/Live tracking.png" alt="Live Driver Tracking" />
        </div>
      </section>

      <section className="steps">
        <h2>How It Works</h2>
        <div className="grid">
          <div className="card">
            <h3>1. Easy Delivery Setup</h3>
            <p>You can add a new delivery on your main dashboard.</p>
          </div>
          <div className="card">
            <h3>2. Informative tracking</h3>
            <p>You can track everything about your delivery, including the status, the driver location relative to the delivery address, logs about the delivery and delivery time.</p>
          </div>
          <div className="card">
            <h3>3. We Handle the Rest</h3>
            <p>Evening pickup, real-time SMS updates, and photo proof of delivery.</p>
          </div>
        </div>
      </section>

      <section className="hero" style={{background: 'linear-gradient(135deg, var(--brand) 0%, var(--brand-light) 100%)', color: 'var(--dark)'}}>
        <div>
          <h1>Same-Day Delivery<br />Across New Jersey</h1>
          <p style={{fontSize: '20px', marginBottom: '40px', maxWidth: '700px'}}>Need it today? We've got you covered. Our same-day delivery service reaches pharmacies throughout New Jersey with reliable, timely service.</p>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', maxWidth: '800px', margin: '0 auto', textAlign: 'left'}}>
            <div style={{background: 'rgba(255,255,255,0.2)', padding: '24px', borderRadius: '16px', backdropFilter: 'blur(10px)'}}>
              <h3 style={{color: 'var(--dark)', marginBottom: '8px', fontSize: '20px'}}>🚚 Metro Areas</h3>
              <p style={{color: 'var(--dark)', fontSize: '16px', margin: '0'}}>Coverage across New Jersey metro regions</p>
            </div>
            <div style={{background: 'rgba(255,255,255,0.2)', padding: '24px', borderRadius: '16px', backdropFilter: 'blur(10px)'}}>
              <h3 style={{color: 'var(--dark)', marginBottom: '8px', fontSize: '20px'}}>⏱️ Same Day</h3>
              <p style={{color: 'var(--dark)', fontSize: '16px', margin: '0'}}>Pick up in morning, deliver by evening</p>
            </div>
            <div style={{background: 'rgba(255,255,255,0.2)', padding: '24px', borderRadius: '16px', backdropFilter: 'blur(10px)'}}>
              <h3 style={{color: 'var(--dark)', marginBottom: '8px', fontSize: '20px'}}>📍 Wide Coverage</h3>
              <p style={{color: 'var(--dark)', fontSize: '16px', margin: '0'}}>Serving pharmacies statewide in NJ</p>
            </div>
          </div>
        </div>
      </section>

      <section className="steps" style={{background: 'linear-gradient(135deg, var(--dark-light) 0%, var(--dark) 100%)'}}>
        <h2 style={{color: 'var(--light)'}}>HIPAA Compliant Professional Drivers</h2>
        <div style={{maxWidth: '800px', margin: '0 auto'}}>
          <div className="card" style={{background: 'rgba(255,255,255,0.95)', color: 'var(--dark)'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap'}}>
              <div style={{flex: '1', minWidth: '300px'}}>
                <h3 style={{color: 'var(--brand)', fontSize: '32px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <span style={{fontSize: '48px'}}>🛡️</span>
                  Trusted & Certified
                </h3>
                <p style={{fontSize: '18px', lineHeight: '1.8', marginBottom: '16px'}}>
                  All Courier-RX drivers are <strong>HIPAA compliant</strong>, fully trained, and certified to handle sensitive pharmacy deliveries. We maintain the highest standards of privacy and security for your patients' information.
                </p>
                <div style={{background: 'rgba(2, 217, 107, 0.1)', padding: '16px', borderRadius: '12px', borderLeft: '4px solid var(--brand)', marginTop: '24px'}}>
                  <p style={{margin: '0', fontWeight: '700', color: 'var(--dark)'}}>✓ All drivers are employees, not freelancers</p>
                  <p style={{margin: '8px 0 0 0', color: 'var(--gray)'}}>Professionally trained, insured, and held to our company standards</p>
                </div>
              </div>
              <div style={{flex: '1', minWidth: '280px'}}>
                <div style={{display: 'grid', gap: '16px'}}>
                  <div style={{background: 'linear-gradient(135deg, var(--brand) 0%, var(--brand-light) 100%)', padding: '20px', borderRadius: '12px', color: 'var(--dark)'}}>
                    <h4 style={{margin: '0 0 8px 0', fontSize: '16px'}}>HIPAA Trained</h4>
                    <p style={{margin: '0', fontSize: '14px'}}>Certified in privacy protocols</p>
                  </div>
                  <div style={{background: 'linear-gradient(135deg, var(--brand) 0%, var(--brand-light) 100%)', padding: '20px', borderRadius: '12px', color: 'var(--dark)'}}>
                    <h4 style={{margin: '0 0 8px 0', fontSize: '16px'}}>Background Checked</h4>
                    <p style={{margin: '0', fontSize: '14px'}}>Thoroughly vetted professionals</p>
                  </div>
                  <div style={{background: 'linear-gradient(135deg, var(--brand) 0%, var(--brand-light) 100%)', padding: '20px', borderRadius: '12px', color: 'var(--dark)'}}>
                    <h4 style={{margin: '0 0 8px 0', fontSize: '16px'}}>Fully Insured</h4>
                    <p style={{margin: '0', fontSize: '14px'}}>Comprehensive coverage</p>
                  </div>
                  <div style={{background: 'linear-gradient(135deg, var(--brand) 0%, var(--brand-light) 100%)', padding: '20px', borderRadius: '12px', color: 'var(--dark)'}}>
                    <h4 style={{margin: '0 0 8px 0', fontSize: '16px'}}>Company Employees</h4>
                    <p style={{margin: '0', fontSize: '14px'}}>Dedicated team members</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing">
        <h2>Simple, Transparent Pricing</h2>
        <p style={{fontSize: '24px', fontWeight: 'bold'}}>$7.99 per delivery</p>
        <p>Metro-wide • No contracts • $2.00 surcharge for cold-chain (waived first 3 months)</p>
      </section>

      <footer>
        <div style={{marginBottom: '16px'}}>
          <a href="#">Support Chat</a>
        </div>
        <p>&copy; 2025 COURIER-RX. All rights reserved.</p>
      </footer>

      {/* Modal for enlarged image */}
      <div className="modal" onClick={handleModalClick}>
        <span className="close" onClick={closeModal}>&times;</span>
        <img className="modal-content" src={modalImageSrc} alt="Enlarged Dashboard" />
      </div>
    </>
  );
}
